import { InternalAdvanceEdge } from '@varo/graphql-schema-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import TransactionListItem from 'shared/components/vxl/TransactionListItem/TransactionListItem';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';
import { formatLocalDate } from 'shared/utils/string/date';

interface TransactionListProps extends PressableAnalyticsProps {
  advanceHistory: InternalAdvanceEdge[];
  item: InternalAdvanceEdge;
  index: number;
  handleTransactionDetails: (id: string) => void;
}

const TransactionList = ({
  item,
  advanceHistory,
  index,
  handleTransactionDetails,
  analyticsId = '',
  customAttributes = null,
}: TransactionListProps) => {
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'advanceDetails',
  });
  const analytics = usePressableAnalytics({ analyticsId, customAttributes });
  const prevAdvanceDate = advanceHistory[index - 1]?.node.dateOfAdvance ?? null;
  const dateOfAdvance = new Date(item?.node?.dateOfAdvance);
  const currentMonth = dateOfAdvance.getMonth();
  const monthAsText = dateOfAdvance.toLocaleString('default', {
    month: 'long',
  });
  const advanceDateHeader = `${monthAsText} ${dateOfAdvance.getFullYear()}`;
  const showMonthAndDivider =
    new Date(prevAdvanceDate).getMonth() !== currentMonth;

  return (
    <View>
      {showMonthAndDivider && index !== 0 ? (
        <View style={styles.sectionDivider} />
      ) : null}
      {!showMonthAndDivider ? (
        <View style={styles.hairLine}>
          <HairLineDivider />
        </View>
      ) : null}

      {showMonthAndDivider ? (
        <SectionDivider labelText={advanceDateHeader} />
      ) : null}

      <TransactionListItem
        titleText={translatedText('listItems.advance')}
        secondaryText={formatLocalDate(item?.node?.dateOfAdvance)}
        amountText={item?.node?.amount?.formattedWithPlusPrefix}
        onPress={() => {
          analytics();
          handleTransactionDetails(item?.node?.id);
        }}
        isPending={false}
        amountStyle="POSITIVE"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  hairLine: {
    paddingHorizontal: theme.spacing.m,
  },
  sectionDivider: {
    backgroundColor: theme.color.background.tertiary,
    height: theme.spacing.s,
  },
});

export default TransactionList;
